// frontend/src/components/BiomeasurementAddModal.js
// Modal component for adding biomeasurement data directly in the flowsheet

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Input,
  Space,
  Typography,
  Alert,
  message,
  Divider
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { usePatientChart } from '../context/PatientChartContext';
import MomentDatePicker from './MomentDatePicker.tsx';

const { Title, Text } = Typography;

/**
 * BiomeasurementAddModal Component
 * Provides a dedicated interface for adding biomeasurement and bioimpedance data to the flowsheet
 */
const BiomeasurementAddModal = ({
  visible,
  onClose,
  onSave,
  patientId,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(moment());
  const [hasFormData, setHasFormData] = useState(false);
  const { refreshVitals } = usePatientChart();

  // Check if form has any data
  const checkFormData = () => {
    const values = form.getFieldsValue();
    const hasData = Object.values(values).some(value => 
      value !== null && value !== undefined && value !== ''
    );
    setHasFormData(hasData);
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedDateTime(moment());
      setHasFormData(false);
    }
  }, [visible, form]);

  // Handle form submission
  const handleSave = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form fields
      const values = await form.validateFields();
      
      // Filter out empty values
      const filteredValues = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
          filteredValues[key] = values[key];
        }
      });

      if (Object.keys(filteredValues).length === 0) {
        message.warning('Please enter at least one biomeasurement parameter.');
        return;
      }

      // Prepare payload for backend - using the biometrics endpoint
      const payload = {
        entryDate: selectedDateTime.toISOString(),
        biometrics: filteredValues
      };

      // Call backend API using the existing biometrics endpoint
      const response = await axios.post(`/patients/${patientId}/flowsheet/biometrics`, payload);

      if (response.data.status === 'success') {
        message.success('Biomeasurement data saved successfully!');

        // Refresh vitals banner (in case biomeasurement data affects displayed metrics)
        refreshVitals();

        // Call parent onSave callback to refresh flowsheet
        if (onSave) {
          onSave();
        }

        // Close modal and reset form
        onClose();
        form.resetFields();
      } else {
        throw new Error(response.data.message || 'Failed to save biomeasurement data');
      }
    } catch (error) {
      console.error('Error saving biomeasurement data:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to save biomeasurement data');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Define biomeasurement parameters based on BiomeasurementInputForm
  const biomeasurementFields = [
    { name: 'biometrics_biomeasurement_waistCircumference', label: 'Waist Circumference', units: 'cm', inputType: 'InputNumber', placeholder: 'cm', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_biomeasurement_hipCircumference', label: 'Hip Circumference', units: 'cm', inputType: 'InputNumber', placeholder: 'cm', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_biomeasurement_neckCircumference', label: 'Neck Circumference', units: 'cm', inputType: 'InputNumber', placeholder: 'cm', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_biomeasurement_weight_inbody', label: 'Weight (InBody)', units: 'kg', inputType: 'InputNumber', placeholder: 'kg', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_biomeasurement_skeletalMuscleMass', label: 'Skeletal Muscle Mass', units: 'kg', inputType: 'InputNumber', placeholder: 'kg', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_biomeasurement_bodyFatMass', label: 'Body Fat Mass', units: 'kg', inputType: 'InputNumber', placeholder: 'kg', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_biomeasurement_percentBodyFat', label: 'Percent Body Fat', units: '%', inputType: 'InputNumber', placeholder: '%', rules: [{ type: 'number', min: 0, max: 100, message: 'Percentage must be between 0 and 100' }] },
    { name: 'biometrics_biomeasurement_waistHipRatio', label: 'Waist-Hip Ratio', inputType: 'InputNumber', placeholder: 'e.g., 0.85', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_biomeasurement_visceralFatLevel', label: 'Visceral Fat Level', inputType: 'InputNumber', placeholder: 'e.g., 10', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_biomeasurement_softLeanMass', label: 'Soft Lean Mass', units: 'kg', inputType: 'InputNumber', placeholder: 'kg', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_biomeasurement_upperLowerBodyBalance', label: 'Upper/Lower Body Balance', inputType: 'Input', placeholder: 'e.g., Balanced' },
    { name: 'biometrics_biomeasurement_inbodyScore', label: 'InBody Score', inputType: 'InputNumber', placeholder: 'e.g., 85', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_biomeasurement_bodyTypeCoordinate', label: 'Body Type Coordinate', inputType: 'Input', placeholder: 'e.g., C-Shape' },
  ];

  return (
    <Modal
      title="Add Biomeasurement Data"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={submitLoading}
          onClick={handleSave}
          disabled={!hasFormData}
          icon={<ClockCircleOutlined />}
        >
          Save Biomeasurement Data
        </Button>
      ]}
      width={800}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Add New Biomeasurement Entry"
          description="Enter body measurements and bioimpedance analysis results for the selected date and time. These measurements help track body composition changes over time."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {/* Date/Time Selection */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Text strong>Entry Date & Time:</Text>
            <br />
            <MomentDatePicker
              showTime
              value={selectedDateTime}
              onChange={setSelectedDateTime}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select date and time"
            />
          </Col>
        </Row>
      </div>

      <Form form={form} layout="vertical" name="biomeasurement_add_form" onValuesChange={checkFormData}>
        <Title level={5}>Biomeasurement & Bioimpedance Parameters</Title>
        
        {/* Body Measurements */}
        <Divider orientation="left">Body Measurements</Divider>
        <Row gutter={16}>
          {biomeasurementFields.slice(0, 4).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                {field.inputType === 'InputNumber' ? (
                  <InputNumber 
                    style={{ width: '100%' }} 
                    placeholder={field.placeholder}
                    min={field.rules?.[0]?.min}
                    max={field.rules?.[0]?.max}
                    precision={1}
                  />
                ) : (
                  <Input 
                    placeholder={field.placeholder}
                  />
                )}
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Body Composition */}
        <Divider orientation="left">Body Composition</Divider>
        <Row gutter={16}>
          {biomeasurementFields.slice(4, 10).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                {field.inputType === 'InputNumber' ? (
                  <InputNumber 
                    style={{ width: '100%' }} 
                    placeholder={field.placeholder}
                    min={field.rules?.[0]?.min}
                    max={field.rules?.[0]?.max}
                    precision={1}
                  />
                ) : (
                  <Input 
                    placeholder={field.placeholder}
                  />
                )}
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* InBody Analysis */}
        <Divider orientation="left">InBody Analysis</Divider>
        <Row gutter={16}>
          {biomeasurementFields.slice(10).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                {field.inputType === 'InputNumber' ? (
                  <InputNumber 
                    style={{ width: '100%' }} 
                    placeholder={field.placeholder}
                    min={field.rules?.[0]?.min}
                    max={field.rules?.[0]?.max}
                    precision={1}
                  />
                ) : (
                  <Input 
                    placeholder={field.placeholder}
                  />
                )}
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form>
    </Modal>
  );
};

export default BiomeasurementAddModal;
